import type {
  MCPServerConfig,
  MCPServerConfigListResponse,
  MCPServerConfigListParams,
  MCPServerHistory,
  MCPServerHistoryParams,
  MCPServer,
  MCPServerStepOptionsParams,
  MCPToolCatalogResponse,
} from '@kizen/api/mcp';
import type { DefaultError, QueryObserverOptions } from '@tanstack/query-core';
import type { AxiosInstance } from 'axios';
import type { Resource } from './resource';
import type { Drop, WithParams, WithRequired } from './types';

import {
  getMCPServers,
  getMCPServerById,
  getMCPTools,
  getMCPServerStepOptions,
  getMCPServerHistory,
} from '@kizen/api/mcp';
import { getResource } from './resource';
import { curry1 } from './utils';

type MCPServerListQuery<TData = MCPServerConfigListResponse> = WithRequired<
  QueryObserverOptions<
    MCPServerConfigListResponse,
    DefaultError,
    TData,
    MCPServerConfigListResponse,
    [Resource<Readonly<['mcp', 'servers']>, MCPServerConfigListParams>]
  >,
  'queryFn' | 'queryKey'
>;

type MCPServerQuery<TData = MCPServerConfig> = WithRequired<
  QueryObserverOptions<
    MCPServerConfig,
    DefaultError,
    TData,
    MCPServerConfig,
    [Resource<Readonly<['mcp', 'server', string]>, undefined>]
  >,
  'queryFn' | 'queryKey'
>;

type MCPToolsQuery<TData = MCPToolCatalogResponse> = WithRequired<
  QueryObserverOptions<
    MCPToolCatalogResponse,
    DefaultError,
    TData,
    MCPToolCatalogResponse,
    [Resource<Readonly<['mcp', 'tools']>, undefined>]
  >,
  'queryFn' | 'queryKey'
>;

type MCPServerStepOptionsQuery<TData = MCPServer[]> = WithRequired<
  QueryObserverOptions<
    MCPServer[],
    DefaultError,
    TData,
    MCPServer[],
    [Resource<Readonly<['mcp', 'step-options']>, MCPServerStepOptionsParams>]
  >,
  'queryFn' | 'queryKey'
>;

type MCPServerHistoryQuery<TData = MCPServerHistory> = WithRequired<
  QueryObserverOptions<
    MCPServerHistory,
    DefaultError,
    TData,
    MCPServerHistory,
    [
      Resource<
        Readonly<['mcp', 'server', string, 'history']>,
        MCPServerHistoryParams
      >,
    ]
  >,
  'queryFn' | 'queryKey'
>;

export const mcpServerListQuery = <TData = MCPServerConfigListResponse>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<MCPServerListQuery<TData>, 'queryFn' | 'queryKey'>,
    MCPServerConfigListParams
  > = {}
): MCPServerListQuery<TData> => {
  const path = ['mcp', 'servers'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getMCPServers(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const mcpServerQuery = <TData = MCPServerConfig>(
  axios: AxiosInstance,
  id: string,
  options: Omit<MCPServerQuery<TData>, 'queryFn' | 'queryKey'> = {}
): MCPServerQuery<TData> => {
  const path = ['mcp', 'server', id] as const;
  const resource = getResource(path, {});

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getMCPServerById(axios, id, { signal });
    },
    ...options,
  };
};

export const mcpToolsQuery = <TData = MCPToolCatalogResponse>(
  axios: AxiosInstance,
  options: Omit<MCPToolsQuery<TData>, 'queryFn' | 'queryKey'> = {}
): MCPToolsQuery<TData> => {
  const path = ['mcp', 'tools'] as const;
  const resource = getResource(path, {});

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const { signal } = context;
      return getMCPTools(axios, { signal });
    },
    ...options,
  };
};

export const mcpServerStepOptionsQuery = <TData = MCPServer[]>(
  axios: AxiosInstance,
  options: WithParams<
    Omit<MCPServerStepOptionsQuery<TData>, 'queryFn' | 'queryKey'>,
    MCPServerStepOptionsParams
  > = {}
): MCPServerStepOptionsQuery<TData> => {
  const path = ['mcp', 'step-options'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getMCPServerStepOptions(axios, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

export const mcpServerHistoryQuery = <TData = MCPServerHistory>(
  axios: AxiosInstance,
  id: string,
  options: WithParams<
    Omit<MCPServerHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
    MCPServerHistoryParams
  > = {}
): MCPServerHistoryQuery<TData> => {
  const path = ['mcp', 'server', id, 'history'] as const;
  const { params, ...queryOpts } = options;
  const resource = getResource(path, { params });

  return {
    queryKey: [resource],
    queryFn: (context) => {
      const {
        queryKey: [qk],
        signal,
      } = context;

      return getMCPServerHistory(axios, id, {
        params: qk.params,
        signal,
      });
    },
    ...queryOpts,
  };
};

class Queries {
  public mcpServerListQuery: <TData = MCPServerConfigListResponse>(
    ...args: Drop<Parameters<typeof mcpServerListQuery<TData>>>
  ) => MCPServerListQuery<TData>;

  public mcpServerQuery: <TData = MCPServerConfig>(
    id: string,
    options?: Omit<MCPServerQuery<TData>, 'queryFn' | 'queryKey'>
  ) => MCPServerQuery<TData>;

  public mcpToolsQuery: <TData = MCPToolCatalogResponse>(
    ...args: Drop<Parameters<typeof mcpToolsQuery<TData>>>
  ) => MCPToolsQuery<TData>;

  public mcpServerStepOptionsQuery: <TData = MCPServer[]>(
    ...args: Drop<Parameters<typeof mcpServerStepOptionsQuery<TData>>>
  ) => MCPServerStepOptionsQuery<TData>;

  public mcpServerHistoryQuery: <TData = MCPServerHistory>(
    id: string,
    options?: WithParams<
      Omit<MCPServerHistoryQuery<TData>, 'queryFn' | 'queryKey'>,
      MCPServerHistoryParams
    >
  ) => MCPServerHistoryQuery<TData>;

  constructor(axios: AxiosInstance) {
    this.mcpServerListQuery = curry1(mcpServerListQuery)(axios);
    this.mcpServerQuery = (id, options) => mcpServerQuery(axios, id, options);
    this.mcpToolsQuery = curry1(mcpToolsQuery)(axios);
    this.mcpServerStepOptionsQuery = curry1(mcpServerStepOptionsQuery)(axios);
    this.mcpServerHistoryQuery = (id, options) =>
      mcpServerHistoryQuery(axios, id, options);
  }
}

export type MCPQueries = Queries;

export const defMCPQueries = (axios: AxiosInstance): MCPQueries => {
  return new Queries(axios);
};
